import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { RadialGauge } from "@/components/Gauge";
import { Check, X, ShieldCheck } from "lucide-react";

/**
 * BriefDocument — PDF-quality executive briefing layout.
 * Single source of truth used by the owner Export page and the public
 * read-only share page, so both always look identical and on-brand.
 */
export function BriefDocument({ brief, testId = "export-brief" }) {
  const { t } = useTranslation();
  const r = brief.recommendation;
  const ranked = [...(brief.alternatives || [])]
    .map((a) => ({ ...a, score: brief.scores?.[a.id] ?? 0 }))
    .sort((a, b) => b.score - a.score);
  const passed = (brief.trust_checks || []).filter((c) => c.status === "pass").length;
  const stamp = brief.decision_updated_at || brief.generated_at;

  return (
    <div className="print-page card-crisp overflow-hidden" data-testid={testId}>
      <div
        className="relative overflow-hidden p-10 text-white"
        style={{
          background:
            "radial-gradient(120% 140% at 10% 0%, hsl(223 100% 30%) 0%, hsl(var(--brand-900)) 55%, hsl(var(--brand-950)) 100%)",
        }}
      >
        <div className="grain absolute inset-0" />
        <div className="relative flex items-center justify-between">
          <Logo dark align="lockup" subtitle={t("auth.overline")} size={44} />
          <span className="text-xs font-semibold uppercase tracking-overline text-white/50">
            {t("export.brief")}
          </span>
        </div>
        <h1 className="relative mt-8 font-display text-3xl font-extrabold leading-tight tracking-tight">
          {brief.title}
        </h1>
        <p className="relative mt-2 max-w-xl text-sm leading-relaxed text-white/70">
          {brief.summary || brief.query}
        </p>
        <p className="relative mt-6 font-mono text-xs text-white/50">
          {t("export.generated", {
            date: new Date(stamp).toLocaleString(),
            ref: String(brief.share_id).slice(0, 8).toUpperCase(),
          })}
        </p>
      </div>

      <div className="p-10">
        <SectionTitle n="01" label={t("export.execSummary")} />
        <div className="mt-4 flex flex-col items-start justify-between gap-6 rounded-lg border border-border bg-secondary/30 p-6 sm:flex-row sm:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-overline text-primary">
              {t("export.recommendation")}
            </p>
            <p className="mt-1 font-display text-xl font-bold">{r.option_name}</p>
            {r.price_estimate && (
              <p className="font-mono text-sm text-muted-foreground">{r.price_estimate}</p>
            )}
          </div>
          <div className="flex gap-8">
            <RadialGauge value={r.decision_score} label={t("export.decisionScore")} sublabel="/ 100" tone="score" />
            <RadialGauge value={r.confidence} label={t("common.confidence")} tone="confidence" />
          </div>
        </div>

        <SectionTitle n="02" label={t("export.rationale")} className="mt-10" />
        <p className="mt-3 text-sm leading-relaxed">{r.why}</p>
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-overline text-success">
              {t("export.strengths")}
            </p>
            <ul className="space-y-1.5">
              {(r.pros || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-overline text-danger">
              {t("export.considerations")}
            </p>
            <ul className="space-y-1.5">
              {(r.cons || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {ranked.length > 0 && (
          <>
            <SectionTitle n="03" label={t("export.alternatives")} className="mt-10" />
            <div className="mt-4 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">{t("export.thNum")}</th>
                    <th className="px-4 py-2.5 text-left font-semibold">{t("export.thOption")}</th>
                    <th className="px-4 py-2.5 text-right font-semibold">{t("export.thScore")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((a, i) => (
                    <tr key={a.id} className="border-t border-border">
                      <td className="px-4 py-2.5 font-mono text-muted-foreground">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium">{a.name}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{a.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <SectionTitle n="04" label={t("export.trustLayer")} className="mt-10" />
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-success/5 p-4 text-sm">
          <ShieldCheck className="h-4 w-4 text-success" />
          <span>{t("export.trustSummary", { passed, total: (brief.trust_checks || []).length })}</span>
        </div>

        {brief.second_opinion && (
          <>
            <SectionTitle n="05" label={t("export.secondOpinion")} className="mt-10" />
            <p className="mt-3 text-sm">
              {t("export.soPick")}{" "}
              <strong>{brief.second_opinion.recommended_option_name}</strong> —{" "}
              {brief.second_opinion.agrees_with_primary
                ? t("export.soAgrees")
                : t("export.soDiverges")}
            </p>
          </>
        )}

        <div className="mt-12 flex items-center justify-between border-t border-border pt-5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            {t("export.footer")}
          </span>
          <span className="font-mono">{t("export.page")}</span>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ n, label, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="font-mono text-xs font-semibold text-primary">{n}</span>
      <h2 className="font-display text-sm font-bold uppercase tracking-overline">{label}</h2>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}
